/**
 * Site Refresh 2026: collection hero "Read more" (A18, sections/collection-hero.liquid).
 * The whole description is in the HTML; CSS clips it (desktop: the card height, phone: 3 lines).
 * This script shows the button only when something is cut, fades the cut edge and toggles it.
 *
 * A26: with 4 or more sub-collections the tiles sit in a sideways row (.rch__track, all links in
 * the HTML). Previous / Next (desktop) scroll it by one tile; without this script the row still
 * scrolls by trackpad, swipe and Tab.
 */
(() => {
	const measure = (root) => {
		const fold = root.querySelector('[data-rch-fold]');
		const button = root.querySelector('[data-rch-more]');
		if (!fold || !button || root.classList.contains('is-expanded')) return;

		const cut = fold.scrollHeight > fold.clientHeight + 1;
		fold.classList.toggle('is-cut', cut);
		button.classList.toggle('is-needed', cut);
		// Without this script: phone always shows the button, desktop for 2+ paragraphs.
		// With it: only when something is cut.
		button.classList.toggle('is-hidden', !cut);
	};

	const initSlider = (root) => {
		const track = root.querySelector('[data-rch-track]');
		const nav = root.querySelector('[data-rch-nav]');
		const prev = nav && nav.querySelector('[data-rch-prev]');
		const next = nav && nav.querySelector('[data-rch-next]');
		if (!track || !prev || !next) return;

		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

		const step = () => {
			const slide = track.querySelector('li');
			const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
			return slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
		};

		const setDisabled = (button, other, disabled) => {
			if (button.disabled === disabled) return;
			// Keep keyboard focus on the arrows when the focused one turns off at an end.
			if (disabled && document.activeElement === button && !other.disabled) other.focus();
			button.disabled = disabled;
		};

		const ends = () => {
			const max = track.scrollWidth - track.clientWidth;
			const atStart = track.scrollLeft <= 1;
			const atEnd = track.scrollLeft >= max - 1;
			track.classList.toggle('is-scrolled', !atStart);
			track.classList.toggle('is-end', max > 1 && atEnd);
			return { overflows: max > 1, atStart, atEnd };
		};

		// Desktop: the row runs on past its column to the screen edge (CSS --rch-bleed).
		const bleed = () => {
			const column = track.parentElement.getBoundingClientRect().right;
			const room = Math.max(0, Math.floor(document.documentElement.clientWidth - column - 8));
			track.style.setProperty('--rch-bleed', `${room}px`);
		};

		const update = () => {
			const { overflows, atStart, atEnd } = ends();
			nav.hidden = !overflows;
			setDisabled(prev, next, !overflows || atStart);
			setDisabled(next, prev, !overflows || atEnd);
		};

		const scroll = (direction) => {
			track.scrollBy({ left: direction * step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
		};
		prev.addEventListener('click', () => scroll(-1));
		next.addEventListener('click', () => scroll(1));

		// Tab onto a tile that is only partly in view (the peek): bring all of it in.
		track.addEventListener('focusin', (event) => {
			const slide = event.target.closest('li');
			if (!slide) return;
			const pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
			const box = track.getBoundingClientRect();
			const rect = slide.getBoundingClientRect();
			let delta = 0;
			if (rect.right > box.right - pad + 1) delta = rect.right - (box.right - pad);
			if (rect.left < box.left + pad - 1) delta = rect.left - (box.left + pad);
			if (delta) track.scrollBy({ left: delta, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
		});

		let timer;
		track.addEventListener('scroll', () => {
			ends();
			clearTimeout(timer);
			timer = setTimeout(update, 80);
		}, { passive: true });

		bleed();
		update();
		window.addEventListener('resize', bleed);
		if ('ResizeObserver' in window) {
			new ResizeObserver(update).observe(track);
		} else {
			window.addEventListener('resize', update);
		}
	};

	const init = (root) => {
		if (root.dataset.rchReady) return;
		root.dataset.rchReady = 'true';

		const button = root.querySelector('[data-rch-more]');
		const label = root.querySelector('[data-rch-more-label]');
		if (button && label) {
			button.addEventListener('click', () => {
				const open = !root.classList.contains('is-expanded');
				root.classList.toggle('is-expanded', open);
				button.setAttribute('aria-expanded', String(open));
				label.textContent = open ? button.dataset.labelLess : button.dataset.labelMore;
				if (!open) {
					measure(root);
					const top = root.getBoundingClientRect().top;
					if (top < 0) root.scrollIntoView({ block: 'start' });
				}
			});
		}

		initSlider(root);

		measure(root);
		if ('ResizeObserver' in window) {
			new ResizeObserver(() => measure(root)).observe(root);
		} else {
			window.addEventListener('resize', () => measure(root));
		}
	};

	const initAll = () => document.querySelectorAll('[data-rch]').forEach(init);

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initAll);
	} else {
		initAll();
	}
	// Fonts change the line count after first paint.
	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(() => document.querySelectorAll('[data-rch]').forEach(measure));
	}
	document.addEventListener('shopify:section:load', initAll);
})();
